/**
 * Site footer: Email, Blog, LinkedIn links and copyright.
 * Not shown on the results page (HelpBlock acts as faux footer there).
 * variant 'results' = 3.5rem padding; 'quiz' = 6.5rem padding.
 */
import React from 'react';

const FOOTER_EMAIL = 'hello@murphytrueman.com';
const FOOTER_BLOG_URL = 'https://blog.murphytrueman.com/';
const FOOTER_LINKEDIN_URL = 'https://www.linkedin.com/in/murphytrueman/';

const footerLinks = [
  { href: `mailto:${FOOTER_EMAIL}`, label: 'Email', ariaLabel: 'Email Murphy Trueman', external: false },
  { href: FOOTER_BLOG_URL, label: 'Blog', ariaLabel: 'Murphy Trueman on Substack', external: true },
  { href: FOOTER_LINKEDIN_URL, label: 'LinkedIn', ariaLabel: 'Murphy Trueman on LinkedIn', external: true },
];

interface FooterProps {
  /** 'results' = 3.5rem padding (results page); 'quiz' = 6.5rem padding (checklist page). Default 'quiz'. */
  variant?: 'results' | 'quiz';
}

export const Footer: React.FC<FooterProps> = ({ variant = 'quiz' }) => {
  const paddingClass =
    variant === 'results'
      ? 'pt-[3.5rem] pb-[3.5rem]'
      : 'pt-[6.5rem] pb-[6.5rem]';

  return (
    <footer
      className={`relative border-t border-slate-200/80 bg-gradient-to-b from-slate-50/80 to-white ${paddingClass} px-4 sm:px-6 lg:px-8`}
      role="contentinfo"
    >
      <div className="max-w-container mx-auto">
        <div className="text-center">
          <nav
            className="flex justify-center gap-8 sm:gap-10 mb-8 flex-wrap"
            aria-label="Footer links"
          >
            {footerLinks.map(({ href, label, ariaLabel, external }) => (
              <a
                key={href}
                href={href}
                {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                className="text-slate-600 hover:text-primary transition-colors duration-200 text-sm font-medium underline-offset-4 hover:underline focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-2 rounded"
                aria-label={ariaLabel}
              >
                {label}
              </a>
            ))}
          </nav>
          <p className="text-slate-600 text-sm mb-8 max-w-xl mx-auto">
            This checklist is a constant work in progress. If you have any feedback, or a tool you'd like to share.{' '}
            <a
              href={`mailto:${FOOTER_EMAIL}`}
              className="text-primary hover:underline underline-offset-4 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-2 rounded"
              aria-label="Email Murphy Trueman"
            >
              Let me know.
            </a>
          </p>
          <p className="text-xs text-slate-400 tracking-wide">
            © 2026 Murphy Trueman, Design Systems for AI
          </p>
        </div>
      </div>
    </footer>
  );
};
